/* eslint-disable no-restricted-syntax -- Comment marker colors (amber for open, emerald for resolved) must stay stable across themes so the affordance is recognisable in light and dark mode. */

/**
 * Shared inline-comment adornments (#2244 gutter, reused for #2250).
 *
 * The transcript editor renders an amber underline + a superscript
 * message-circle icon on any Slate leaf that carries an open comment,
 * or an emerald equivalent when only resolved comments remain. That
 * logic lived inside `paragraph-leaf.tsx` and was word-shaped: one
 * comment could span multiple word leaves, and the icon rendered on
 * the leaf that ends the range.
 *
 * Ticket #2250 needs the same treatment on caption segments. Copying
 * the block into `scriptix-caption-editor.tsx` is what the ticket
 * explicitly forbids ("gutter reused, not copied"). Extracting the
 * lookup + label + click-handler into a hook and the underline +
 * icon into a component keeps the visual identical across editors
 * and gives us one place to fix if the design shifts.
 *
 * The hook is deliberately editor-agnostic: it takes a `[start, stop]`
 * range in ms and asks the `CommentsProvider` from
 * `comments-context.tsx` for the surrounding state. Word leaves pass
 * word timestamps; caption leaves pass segment timestamps. Same call.
 */

import React from 'react';
import { CheckCircle2, MessageCircle } from 'lucide-react';

import { useCommentsContext } from './comments-context.tsx';

export type LeafCommentAdornments = {
  /** Underline colour hex for the leaf's boxShadow; undefined = no underline. */
  underlineColor: string | undefined;
  /** Background tint for the leaf; undefined = leave background untouched. */
  tint: string | undefined;
  /** True when the leaf ends the range of any comment (icon anchor point). */
  hasEndingComment: boolean;
  /** True when the leaf's ending-comment set is fully resolved. */
  iconIsResolved: boolean;
  /** Icon pill background colour. Varies with resolved + tone (#2246). */
  iconBg: string;
  /** Screen-reader label for the icon. */
  iconLabel: string | undefined;
  /** Count on the icon when > 1 comments end here. */
  iconCount: number;
  /** Open modal in view mode for the range ending here. */
  onIconClick: (e: React.SyntheticEvent) => void;
};

export function useLeafCommentAdornments(
  start: number | undefined,
  stop: number | undefined
): LeafCommentAdornments {
  const commentsCtx = useCommentsContext();

  const openWord = commentsCtx ? commentsCtx.commentsAtWord(start, stop) : [];
  const openEnd = commentsCtx ? commentsCtx.commentsEndingAt(stop) : [];
  const resolvedWord =
    !openWord.length && commentsCtx
      ? commentsCtx.resolvedCommentsAtWord(start, stop)
      : [];
  const resolvedEnd =
    !openEnd.length && commentsCtx
      ? commentsCtx.resolvedCommentsEndingAt(stop)
      : [];

  const hasOpen = openWord.length > 0;
  const hasResolvedOnly = !hasOpen && resolvedWord.length > 0;
  const endingComments = openEnd.length > 0 ? openEnd : resolvedEnd;
  const iconIsResolved = openEnd.length === 0;

  // #2246: 'muted' palette for comments shown read-only on the
  // opposite pane of split view. Slate 500/400 instead of amber/
  // emerald so the reader can tell "this one belongs to the other
  // pane" at a glance, without hovering.
  const muted = commentsCtx?.tone === 'muted';
  const underlineColor = hasOpen
    ? muted
      ? '#64748b'
      : '#f59e0b'
    : hasResolvedOnly
      ? muted
        ? '#94a3b8'
        : '#10b981'
      : undefined;
  const tint = hasOpen
    ? muted
      ? 'rgba(100, 116, 139, 0.14)'
      : 'rgba(245, 158, 11, 0.18)'
    : hasResolvedOnly
      ? muted
        ? 'rgba(148, 163, 184, 0.12)'
        : 'rgba(16, 185, 129, 0.14)'
      : undefined;

  const iconCount = endingComments.length;
  const iconLabel =
    iconCount > 0
      ? iconIsResolved
        ? `${iconCount} resolved comment${iconCount > 1 ? 's' : ''}`
        : `${iconCount} comment${iconCount > 1 ? 's' : ''}`
      : undefined;
  // Matching palette for the pill background — slate for the opposite
  // pane, amber/emerald otherwise. Keeps the icon visually distinct at
  // a glance (#2246 AC).
  const iconBg = iconIsResolved
    ? muted
      ? '#94a3b8'
      : '#10b981'
    : muted
      ? '#64748b'
      : '#f59e0b';

  const onIconClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Dev's gutter listens to `transcript-comments:focus` with
    // `{ commentId, iconRect }`. The first ending comment is the
    // anchor target; rect comes from the icon's own bounding box so
    // the floating card lands next to the glyph the user clicked.
    const target = endingComments[0];
    if (!target) return;
    const rect = (e.currentTarget as Element).getBoundingClientRect();
    window.dispatchEvent(
      new CustomEvent('transcript-comments:focus', {
        detail: {
          commentId: target.id,
          iconRect: {
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right
          }
        }
      })
    );
  };

  return {
    underlineColor,
    tint,
    hasEndingComment: endingComments.length > 0,
    iconIsResolved,
    iconBg,
    iconLabel,
    iconCount,
    onIconClick
  };
}

/**
 * The floating superscript icon rendered when a leaf ends a comment
 * range. Purely visual — the caller decides whether to render it.
 * Slate-void hints keep it out of the editor's selection model so a
 * click on the icon doesn't drop the caret inside it.
 */
export function LeafCommentIcon({
  adornments
}: {
  adornments: LeafCommentAdornments;
}) {
  if (!adornments.hasEndingComment) return null;
  return (
    <span
      contentEditable={false}
      suppressContentEditableWarning
      data-slate-void="true"
      unselectable="on"
      draggable={false}
      role="button"
      aria-label={adornments.iconLabel}
      tabIndex={0}
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseUp={e => e.stopPropagation()}
      onClick={adornments.onIconClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          adornments.onIconClick(e);
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'super',
        cursor: 'pointer',
        marginLeft: 2,
        padding: '1px 4px',
        borderRadius: 999,
        backgroundColor: adornments.iconBg,
        color: '#ffffff',
        fontSize: 9,
        lineHeight: 1,
        userSelect: 'none'
      }}
    >
      {adornments.iconIsResolved ? (
        <CheckCircle2 size={10} aria-hidden="true" />
      ) : (
        <MessageCircle size={10} aria-hidden="true" />
      )}
      {adornments.iconCount > 1 && (
        <span style={{ marginLeft: 2 }}>{adornments.iconCount}</span>
      )}
    </span>
  );
}
